import {
  everyStateMlsComplianceData,
  EveryStateOriginatingSystemNames,
} from '../constants/compliance/everyStateMlsComplianceData'
import * as Sentry from '@sentry/browser'
import { useEffect, useState } from 'react'
import { ListingDetailsListing } from 'src/types/listings'

/*
  as of 8/19/22 the airtable (the data source everystate gave us) did not include the actual mls originating system names that we will see in 
  the listings data, so the object keys (originating system names) had to be manually updated.  displayLogo field also had
  to be manually updated. HTML string was also manually created
*/

export interface MlsComplianceData {
  mlsAbbreviation: string
  mlsName: string
  disclaimer: string
  copyright: string
  copyrightMarkup?: string
  displayLogo: string
}

const defaultData: MlsComplianceData = {
  mlsAbbreviation: '',
  mlsName: '',
  disclaimer: '',
  copyright: '',
  displayLogo: '',
}

// create a type to reflect the subset of Listings data that we need here
export type ListingSourceInfo = Pick<
  ListingDetailsListing,
  'source' | 'originatingSystemName' | 'disclaimer'
>

export function getMlsComplianceData(
  listing: ListingSourceInfo
): MlsComplianceData {
  try {
    if (
      listing?.source?.name === 'EveryState' &&
      listing?.originatingSystemName
    ) {
      return (
        everyStateMlsComplianceData[listing?.originatingSystemName] ??
        defaultData
      )
    }
    if (listing?.source?.name === 'ListHub') {
      // for listhub listings, disclaimers will come from listing details API data, not from static JS object
      return {
        ...defaultData,
        mlsName: 'ListHub',
        mlsAbbreviation: 'ListHub',
        disclaimer: listing?.disclaimer,
      }
    }
    return defaultData
  } catch (e) {
    Sentry.captureException(e)
    return defaultData
  }
}

export const useComplianceData = (listing: ListingSourceInfo) => {
  const [complianceData, setComplianceData] =
    useState<MlsComplianceData>(defaultData)
  useEffect(() => {
    const data = getMlsComplianceData(listing)
    setComplianceData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.source, listing.disclaimer, listing.originatingSystemName])
  return complianceData
}

export const getLinkToDisclaimersPage = (
  originatingSystemName: EveryStateOriginatingSystemNames | null
) => {
  if (originatingSystemName) {
    return `/mls-disclaimers#${originatingSystemName}`
  }
  return '/mls-disclaimers'
}
